import {type AvmSpecSegment, SEGMENT} from "../reva-spec";
import {REVA_SEGMENTS, type RevaSegment} from "../valuation-data";

/**
 * Differenzierte Objekttypen (building_type) je Kauf-Segment, wie sie die AVM-Spec unter VALUE kennt. 33
 * („EZFH unspezifisch") gibt es nur in COMPARATIVE_VALUE, ist aber eindeutig ein Ein-/Zweifamilienhaus.
 */
const SEGMENT_BY_BUILDING_TYPE: Readonly<Record<number, AvmSpecSegment>> = {
    7: SEGMENT.EZFH_K,
    8: SEGMENT.EZFH_K,
    9: SEGMENT.EZFH_K,
    10: SEGMENT.EZFH_K,
    33: SEGMENT.EZFH_K,
    11: SEGMENT.MFHWGH_K,
    19: SEGMENT.MFHWGH_K,
};

/**
 * Das Kauf-Segment, zu dem ein differenzierter Objekttyp gehört.
 * @param buildingType Wert von building_type
 * @return EZFH_K oder MFHWGH_K, sonst null
 */
export function segmentFromBuildingType(buildingType: unknown): AvmSpecSegment | null {
    const code = typeof buildingType === "string" && buildingType.trim() ? Number(buildingType) : buildingType;
    return typeof code === "number" ? (SEGMENT_BY_BUILDING_TYPE[code] ?? null) : null;
}

/**
 * Segment eines Exposés, sofern REVA es führt. Liefert die Extraktion ein Segment, zählt nur dieses, weil
 * building_type nichts über Kauf oder Miete sagt: ein Miet-Exposé für ein Haus (EZFH_M) wird nicht zum
 * Kauf-Segment. Nur ohne geliefertes Segment wird aus dem differenzierten Objekttyp abgeleitet (#19159).
 * @param extractedSegment Segment aus der Extraktion (oder null)
 * @param buildingType building_type aus der Extraktion (oder null)
 * @return das Segment, oder null wenn keines bestimmbar ist oder REVA es nicht führt
 */
export function resolveExposeSegment(extractedSegment: unknown, buildingType: unknown): AvmSpecSegment | null {
    if (typeof extractedSegment === "string" && extractedSegment) {
        return isRevaSegment(extractedSegment) ? extractedSegment : null;
    }
    return segmentFromBuildingType(buildingType);
}

/**
 * Whether a value is one of the segments REVA works in. Anything else — GEWERBE_M and the rest of the
 * Immobrain world — has no spec to render it with. Mirrors `SegmentResolver.isValid` in reva-rest (#19444).
 * @param value the value to check
 * @return true if REVA supports this segment
 */
export function isRevaSegment(value: unknown): value is RevaSegment {
    return typeof value === "string" && REVA_SEGMENTS.some((segment) => segment === value);
}

/**
 * The layer an effective segment came out of. The names match `ParameterSource`, so `getResourceColor`
 * colours the segment row like every other input; "fallback" is the anchor no layer carries.
 */
export type SegmentOrigin = "reva" | "initialValuation" | "inspection" | "immodrop" | "fallback";

/** The segment each layer carries, in the order the chain consults them. */
export interface SegmentSources {
    /** the REVA override of the Overview tab (`parametersInReva.segment`) */
    override?: string | null;
    initialValuation?: string | null;
    inspection?: string | null;
    /** the exposé segment, as {@link resolveExposeSegment} reads it out of the Immodrop layer */
    immodrop?: string | null;
}

const SEGMENT_CHAIN: ReadonlyArray<readonly [SegmentOrigin, keyof SegmentSources]> = [
    ["reva", "override"],
    ["initialValuation", "initialValuation"],
    ["inspection", "inspection"],
    ["immodrop", "immodrop"],
];

/**
 * Whether the sources disagree about the segment (#19444). An override resolves it — that is what
 * setting one means; without one, every source has to name the same. Unsupported values are skipped
 * as in the chain, and only the four chain layers count: nothing else is a source for the segment.
 * @param sources the segment each layer carries
 * @return true when two sources name different segments and nothing has decided between them
 */
export function hasSegmentConflict(sources: SegmentSources): boolean {
    if (isRevaSegment(sources.override)) return false;
    const named = [sources.initialValuation, sources.inspection, sources.immodrop].filter(isRevaSegment);
    return new Set(named).size > 1;
}

/**
 * Which layer the effective segment comes from — attribution, not resolution (#19444): the backend
 * computes the value, this only names its layer, walking the same chain (override → initial
 * valuation → inspection → exposé → anchor). Its own order is needed because `ALL_SOURCES` ranks the
 * inspection above the initial valuation, which is the other way round for the segment.
 * @param effectiveSegment the segment the backend reports for the valuation
 * @param sources the segment each layer carries
 * @return the layer that decided, or "fallback" for the technical anchor
 */
export function resolveSegmentOrigin(
    effectiveSegment: string | null | undefined,
    sources: SegmentSources,
): SegmentOrigin {
    const chain = SEGMENT_CHAIN.map(([origin, key]) => [origin, sources[key]] as const);

    const decided = chain.find(([, value]) => isRevaSegment(value));
    if (decided && decided[1] === effectiveSegment) return decided[0];

    // The chain does not explain the value — a snapshot serves its frozen segment next to layers that
    // are not all frozen with it. Then name the layer that carries the value rather than the anchor.
    return chain.find(([, value]) => value != null && value === effectiveSegment)?.[0] ?? "fallback";
}

export interface SegmentBuildingTypeRentedData {
    segment?: string | null;
    building_type?: number | null;
    prop_rented?: boolean | null;
}

/**
 * Select the display text for the (differentiated) building type of the given segment, e.g. for
 * a header describing the object.
 * @param payload segment/building_type/prop_rented of the current object
 * @param t translation function
 * @return description, or null if no description possible due to missing data
 */
export function segmentBuildingTypeDescription(
    payload: SegmentBuildingTypeRentedData | null | undefined,
    t: (key: string) => string,
): string | null {
    if (!payload) return null;
    const {segment, building_type: buildingType, prop_rented: rented} = payload;
    if (!segment) return null;

    const segmentMapping: Record<string, {default: string; types?: Record<number, string>}> = {
        [SEGMENT.EZFH_K]: {
            default: "object.differentiatedBuildingTypes.EZFH_K",
            types: {7: "EZFH_K_7", 8: "EZFH_K_8", 9: "EZFH_K_9", 10: "EZFH_K_10"},
        },
        [SEGMENT.MFHWGH_K]: {
            default: "object.differentiatedBuildingTypes.MFHWGH_K",
            types: {11: "MFHWGH_K_11", 19: "MFHWGH_K_19"},
        },
        [SEGMENT.WHG_K]: {
            default: "object.differentiatedBuildingTypes.WHG_K",
        },
        [SEGMENT.WHG_M]: {
            default: "object.differentiatedBuildingTypes.WHG_M",
        },
        [SEGMENT.GRST_K]: {default: "object.differentiatedBuildingTypes.GRST_K"},
    };

    const segmentData = segmentMapping[segment];
    if (!segmentData) return null;

    if (segment === SEGMENT.WHG_K && rented) {
        return t("object.differentiatedBuildingTypes.WHG_K_rented");
    }

    if (segmentData.types && buildingType != null && segmentData.types[buildingType]) {
        return t(`object.differentiatedBuildingTypes.${segmentData.types[buildingType]}`);
    }

    return t(segmentData.default);
}

/** A segment one of the sources offers, together with the layer it comes from. */
export interface SegmentCandidate {
    origin: SegmentOrigin;
    segment: RevaSegment;
}

/**
 * The segments the sources below the override offer, for resolving a conflict by hand. In the order
 * of the chain and each segment only once, named after its strongest layer — two sources agreeing is
 * one choice, not two. The override itself is left out: it is what such a choice writes, so offering
 * it would mean offering the current state as an option.
 * @param sources the segment each layer carries
 * @return the distinct segments on offer, strongest source first
 */
export function listSegmentCandidates(sources: SegmentSources): ReadonlyArray<SegmentCandidate> {
    const seen = new Set<string>();
    return SEGMENT_CHAIN.slice(1).flatMap(([origin, key]) => {
        const segment = sources[key];
        if (!isRevaSegment(segment) || seen.has(segment)) return [];
        seen.add(segment);
        return [{origin, segment}];
    });
}
